<template>
    <component
        :is="previewComponent"
        :meta-overwrites="props.metaOverwrites"
        :master-template="props.masterTemplate"
        @setInStreamValue="(value: boolean) => emit('setInStreamValue', value)"
    >
        <slot />
    </component>
</template>

<script lang="ts">
type MetaAdPreviewTypes =
    | "news-feed"
    | "news-feed-desktop"
    | "news-feed-video"
    | "news-feed-tab"
    | "facebook-reel-mobile"
    | "facebook-reel"
    | "facebook-story"
    | "facebook-in-stream-video-mobile"
    | "facebook-in-stream-video-desktop"
    | "facebook-in-stream-banner-mobile"
    | "facebook-in-stream-banner-desktop"
    | "facebook-in-stream-full-video"
    | "facebook-in-stream-full-banner"
    | "instagram-feed"
    | "instagram-reel"
    | "instagram-story";

export const META_AD_PREVIEW_OPTIONS = {
    "instagram-feed": {
        label: "Instagram Feed",
        value: "instagram-feed",
        width: 318,
        maxHeight: 398,
        minHeight: 90
    },
    "instagram-reel": {
        label: "Instagram Reel",
        value: "instagram-reel",
        width: 318,
        height: 567
    },
    "instagram-story": {
        label: "Instagram Story",
        value: "instagram-story",
        width: 318,
        height: 567
    },
    "facebook-reel": {
        label: "Facebook Reel",
        value: "facebook-reel",
        width: 318,
        height: 567
    },

    "facebook-story": {
        label: "Facebook Story",
        value: "facebook-story",
        width: 318,
        height: 567
    },
    "news-feed": {
        label: "Facebook Feed",
        subLabel: "(mobile)",
        value: "news-feed",
        width: 318,
        maxHeight: 398,
        minHeight: 90
    },
    "news-feed-desktop": {
        label: "Facebook Feed",
        subLabel: "(desktop)",
        value: "news-feed-desktop",
        width: 500,
        maxHeight: 398,
        minHeight: 90
    },
    "facebook-reel-mobile": {
        label: "Facebook Video Feed",
        subLabel: "(fullscreen - mobile)",
        value: "facebook-reel-mobile",
        width: 318,
        height: 567
    },
    "news-feed-video": {
        label: "Facebook Video Feed",
        subLabel: "(videos - mobile)",
        value: "news-feed-video",
        width: 318,
        maxHeight: 398,
        minHeight: 90
    },
    "news-feed-tab": {
        label: "Facebook Video Feed",
        subLabel: "(video tab - mobile)",
        value: "news-feed-tab",
        width: 318,
        maxHeight: 398,
        minHeight: 90
    },
    "facebook-in-stream-video-mobile": {
        label: "Facebook In-Stream Video",
        subLabel: "(mobile)",
        value: "facebook-in-stream-video-mobile",
        width: 318,
        minHeight: 220,
        maxHeight: 400
    },
    "facebook-in-stream-video-desktop": {
        label: "Facebook In-Stream Video",
        subLabel: "(desktop)",
        value: "facebook-in-stream-video-desktop",
        isDesktop: true,
        width: 500,
        minHeight: 220,
        maxHeight: 400
    },
    "facebook-in-stream-banner-mobile": {
        label: "Facebook In-Stream Banner",
        subLabel: "(mobile)",
        value: "facebook-in-stream-banner-mobile",
        width: 318,
        minHeight: 220,
        maxHeight: 400
    },
    "facebook-in-stream-banner-desktop": {
        label: "Facebook In-Stream Banner",
        subLabel: "(desktop)",
        value: "facebook-in-stream-banner-desktop",
        width: 500,
        minHeight: 220,
        maxHeight: 400
    },
    "facebook-in-stream-full-video": {
        label: "Facebook In-Stream Video",
        subLabel: "(mobile fullscreen)",
        value: "facebook-in-stream-full-video",
        width: 318,
        inStreamWidth: 75,
        maxHeight: 400,
        minHeight: 180
    },
    "facebook-in-stream-full-banner": {
        label: "Facebook In-Stream Banner",
        subLabel: "(mobile fullscreen)",
        value: "facebook-in-stream-full-banner",
        width: 318,
        inStreamWidth: 75,
        maxHeight: 400,
        minHeight: 180
    }
} as const as {
    readonly [K in MetaAdPreviewTypes]: {
        readonly label: string;
        readonly subLabel?: string;
        readonly value: K;
        readonly width: number;
        readonly height?: number;
        readonly minHeight?: number;
        readonly maxHeight?: number;
        readonly inStreamWidth?: number;
    };
};
</script>

<script lang="ts" setup>
import { computed } from "vue";
import type { MasterTemplate } from "hoxton-types";

import FacebookInStreamFull from "@/components/Campaign/AdPreview/FacebookInStreamFull.vue";
import FacebookNewsFeed from "@/components/Campaign/AdPreview/FacebookNewsFeed.vue";
import FacebookStory from "@/components/Campaign/AdPreview/FacebookStory.vue";
import FacebookReel from "@/components/Campaign/AdPreview/FacebookReel.vue";
import InstagramStory from "@/components/Campaign/AdPreview/InstagramStory.vue";
import InstagramReel from "@/components/Campaign/AdPreview/InstagramReel.vue";
import InstagramFeed from "@/components/Campaign/AdPreview/InstagramFeed.vue";
import FacebookInStream from "@/components/Campaign/AdPreview/FacebookInStream.vue";
import type { MetaOverwrites } from "@/types/types";

const componentMap = {
    "news-feed": FacebookNewsFeed,
    "news-feed-desktop": FacebookNewsFeed,
    "news-feed-video": FacebookNewsFeed,
    "news-feed-tab": FacebookNewsFeed,
    "facebook-reel-mobile": FacebookReel,
    "facebook-reel": FacebookReel,
    "facebook-story": FacebookStory,
    "facebook-in-stream-video-mobile": FacebookInStream,
    "facebook-in-stream-video-desktop": FacebookInStream,
    "facebook-in-stream-banner-mobile": FacebookInStream,
    "facebook-in-stream-banner-desktop": FacebookInStream,
    "facebook-in-stream-full-video": FacebookInStreamFull,
    "facebook-in-stream-full-banner": FacebookInStreamFull,
    "instagram-story": InstagramStory,
    "instagram-feed": InstagramFeed,
    "instagram-reel": InstagramReel
} as Record<MetaAdPreviewTypes, object>;

const props = defineProps<{
    metaOverwrites: MetaOverwrites;
    masterTemplate: MasterTemplate;
    adPreviewType: MetaAdPreviewTypes;
}>();

const previewComponent = computed(() => componentMap[props.adPreviewType]);

const emit = defineEmits(["setInStreamValue"]);
</script>
